import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { ViewStoreErrorHandler } from "@intrasync/stores/utils";
import type {
  Account,
  Job,
  Product,
  Panel,
  quotes,
  quoteLineItems as QuoteLineItemType,
  salesOrders,
  salesOrderLineItems as SalesOrderLineItemType,
  ProductionScheduleData,
  TableStats,
  ConversionInfo,
  CreatedSalesOrderInfo,
  StructureWithStatus,
  ConvertedStructureInfo,
  manholeQuoteConversionResponse,
} from "./types";
import type { customer } from "types/api-types/crm/customers/customers.types";
import type { manholeStructure } from "types/api-types/estimating/manhole-structures/manhole-structures.types";

export default class SalesOrderConversionViewStore {
  rootStore: RootStore;
  errorHandler: ViewStoreErrorHandler;

  // Observables - Step Management
  currentStep = 1;
  isLoading = false;
  error: string | null = null;

  // Observables - Entity IDs
  selectedQuoteId: string | number | undefined = undefined;
  activeSalesOrderId: number | undefined = undefined;

  // Observables - UI State
  panelTableAssignments: { [key: string]: string } = {};
  scheduleDate: string | null = null;

  // Track panel IDs created in current workflow
  createdPanelIds: number[] = [];

  // Observables - Line Item Selection (for partial conversion)
  selectedLineItemIds: string[] = [];

  // Conversion tracking: maps quoteLineItemId -> conversion info
  conversionMap: Map<string, ConversionInfo> = new Map();

  // Sales orders created from the current quote
  createdSalesOrdersForQuote: CreatedSalesOrderInfo[] = [];

  // Structure group expansion state
  expandedStructures: Set<string> = new Set();

  // Post-conversion dialog state
  showPostConversionPrompt: boolean = false;

  // NEW: Structure-based selection (replaces line item selection for API conversion)
  selectedStructureIds: string[] = [];

  // NEW: Conversion form inputs
  selectedCustomerId: string = "";
  purchaseOrderNumber: string = "";
  conversionNotes: string = "";

  // Per-structure settings for anticipationDate and priority
  structureSettings: Map<string, { anticipationDate: string; priority: number }> = new Map();

  // NEW: Track converted structures (structureId UUID -> conversion info)
  convertedStructureMap: Map<string, ConvertedStructureInfo> = new Map();

  // NEW: Last conversion response
  lastConversionResponse: manholeQuoteConversionResponse | null = null;

  // NEW: Grouping mode - when true, all selected structures go into ONE sales order
  groupStructuresIntoOne: boolean = true;

  // Mock Storage for demo
  private mockSalesOrders: salesOrders[] = [];
  private mockSalesOrderLineItems: SalesOrderLineItemType[] = [];
  private mockPanels: Panel[] = [];
  private nextMockId = 2000;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, errorHandler: false });
    this.rootStore = rootStore;

    // Initialize standardized error handling
    this.errorHandler = new ViewStoreErrorHandler(
      rootStore,
      "SalesOrderConversionViewStore",
      {
        enableErrorNotifications: true,
        enableReactionBatching: true,
        debugMode: false,
      }
    );
  }

  // Computed Values - Access entities from ServiceStores
  get availableQuotes(): quotes[] {
    const store = this.rootStore.manholeQuotesStore as ServiceStore;
    const storeQuotes = (store?.entities || []) as unknown as quotes[];

    console.log("Manhole quotes in store:", storeQuotes.length);

    // Filter for quotes that are in a state ready for conversion
    // Also filter out any duplicates by quoteNumber
    const seenQuoteNumbers = new Set<string>();
    const filtered = storeQuotes.filter((quote) => {
      // Check if we've already seen this quote number (duplicate check)
      if (seenQuoteNumbers.has(quote.quoteNumber)) {
        console.log(`Duplicate quote found: ${quote.quoteNumber}, skipping`);
        return false;
      }
      seenQuoteNumbers.add(quote.quoteNumber);

      // Manhole quotes use lowercase status values
      const isConvertible =
        quote.status === "accepted" ||
        quote.status === "sent" ||
        quote.status === "draft";

      return isConvertible;
    });

    console.log("Available quotes after filtering:", filtered.length);
    return filtered;
  }

  get selectedQuote(): quotes | null {
    if (!this.selectedQuoteId) return null;
    const store = this.rootStore.manholeQuotesStore as ServiceStore;
    const entities = (store?.entities || []) as unknown as quotes[];

    // Check store - manholeQuote uses string IDs
    const storeQuote = entities.find((q) => q.id === String(this.selectedQuoteId) || q.id === this.selectedQuoteId);
    return storeQuote || null;
  }

  get quoteLineItems(): QuoteLineItemType[] {
    if (!this.selectedQuoteId) return [];
    const store = this.rootStore.manholeQuoteItemsStore as ServiceStore;

    // Get line items from store
    const storeLineItems = (store?.entities || []) as unknown as QuoteLineItemType[];

    console.log("Manhole quote items in store:", storeLineItems.length);
    console.log("Looking for quoteId:", this.selectedQuoteId, typeof this.selectedQuoteId);

    const filtered = storeLineItems.filter((item) => {
      // manholeQuoteItem uses string quoteId
      const matches = item.quoteId === String(this.selectedQuoteId) || item.quoteId === this.selectedQuoteId;
      if (matches) {
        console.log("Found matching line item:", item);
      }
      return matches;
    });

    console.log("Filtered line items for quote:", filtered.length);
    return filtered;
  }

  get activeSalesOrder(): salesOrders | null {
    if (!this.activeSalesOrderId) return null;
    const store = this.rootStore.salesOrdersStore as ServiceStore;
    const entities = (store?.entities || []) as unknown as salesOrders[];

    // Check store first
    const storeSO = entities.find(
      (so) => so.id === this.activeSalesOrderId
    );
    if (storeSO) return storeSO;

    // Check mock sales orders
    const mockSO = this.mockSalesOrders.find(
      (so) => so.id === this.activeSalesOrderId
    );

    return mockSO || null;
  }

  get salesOrderLineItems(): SalesOrderLineItemType[] {
    if (!this.activeSalesOrderId) return [];
    const store = this.rootStore.salesOrderLineItemsStore as ServiceStore;
    const entities = (store?.entities || []) as unknown as SalesOrderLineItemType[];
    return entities.filter(
      (item) => item.salesOrderId === this.activeSalesOrderId
    );
  }

  get generatedPanels(): Panel[] {
    // Get panels from the panel ServiceStore that were created in this workflow
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const storePanels = panelStore?.entities
      ?.filter((entity: unknown) => {
        const panel = entity as Panel;
        return this.createdPanelIds.includes(panel.id as number);
      })
      .map((entity: unknown) => entity as Panel) || [];

    // If no panels in store, use mock panels
    if (storePanels.length === 0) {
      return this.mockPanels.filter(panel =>
        this.createdPanelIds.includes(panel.id as number)
      );
    }

    return storePanels;
  }

  get accounts(): Account[] {
    const store = this.rootStore.customersStore as ServiceStore;
    const storeAccounts = store?.entities?.map((entity: unknown) => entity as Account) || [];

    // If no accounts in store, use mock data from QuoteGenerationViewStore
    if (storeAccounts.length === 0) {
      const quoteGenerationStore = this.rootStore.quoteGenerationViewStore as any;
      // QuoteGenerationViewStore already returns mock accounts when store is empty
      return quoteGenerationStore?.accounts || [];
    }

    return storeAccounts;
  }

  get projects(): Array<{
    id: number;
    projectName: string;
    accountId?: number;
  }> {
    const store = this.rootStore.jobStore as ServiceStore;
    const storeProjects = store?.entities?.map((entity: unknown) => {
      const job = entity as Job;
      return {
        id: job.id,
        projectName: job.name || "",
        accountId: job.accountId,
      };
    }) || [];

    // If no projects in store, use mock data from QuoteGenerationViewStore
    if (storeProjects.length === 0) {
      const quoteGenerationStore = this.rootStore.quoteGenerationViewStore as any;
      // QuoteGenerationViewStore already returns mock projects when store is empty
      return quoteGenerationStore?.projects || [];
    }

    return storeProjects;
  }

  get products(): Product[] {
    const store = this.rootStore.productsStore as ServiceStore;
    const storeProducts = store?.entities?.map((entity: unknown) => entity as Product) || [];

    // If no products in store, use mock data from QuoteGenerationViewStore
    if (storeProducts.length === 0) {
      const quoteGenerationStore = this.rootStore.quoteGenerationViewStore as any;
      // QuoteGenerationViewStore already returns mock products when store is empty
      return quoteGenerationStore?.products || [];
    }

    return storeProducts;
  }

  get tableList(): TableStats[] {
    const castingMapLayoutStore = this.rootStore
      .castingMapLayoutStore as ServiceStore;
    return (
      (castingMapLayoutStore?.entities
        ?.slice()
        ?.filter(
          (table: unknown) =>
            (table as { startDate: string; endDate: string }).startDate <=
            this.castingDisplayDate &&
            (((table as { startDate: string; endDate: string }).endDate !==
              "1970-01-01" &&
              (table as { startDate: string; endDate: string }).endDate >=
              this.castingDisplayDate) ||
              (table as { startDate: string; endDate: string }).endDate ===
              "1970-01-01")
        ) as unknown as TableStats[]) || []
    );
  }

  get availableTablesFromList() {
    const uniqueTableNumbers = [
      ...new Set(this.tableList.map((i: TableStats) => String(i.tableNumber))),
    ];

    // If no tables from store, provide mock table numbers for demo
    if (uniqueTableNumbers.length === 0) {
      return ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
    }

    return uniqueTableNumbers.sort((a, b) => Number(a) - Number(b));
  }

  get castingDisplayDate(): string {
    return this.scheduleDate || new Date().toISOString().split("T")[0];
  }

  get workflowProgress(): number {
    return ((this.currentStep - 1) / 3) * 100;
  }

  // Computed - Get all structures from the store
  get allStructures(): manholeStructure[] {
    const manholeStructuresStore = this.rootStore.manholeStructuresStore as ServiceStore;
    return (manholeStructuresStore?.entities || []) as unknown as manholeStructure[];
  }

  // Computed - Get structures for the selected quote's project
  get quoteStructures(): manholeStructure[] {
    const quote = this.selectedQuote;
    if (!quote?.manholeProjectId) return [];
    return this.allStructures.filter((s) => s.projectId === quote.manholeProjectId);
  }

  // Computed - Lookup map from structure UUID to readable name (e.g., "MH-001")
  get structureNameMap(): Map<string, string> {
    const nameMap = new Map<string, string>();
    this.allStructures.forEach((s) => {
      nameMap.set(s.id, s.structureId);
    });
    return nameMap;
  }

  // Computed - Get active customers for dropdown
  get activeCustomers(): customer[] {
    const customersStore = this.rootStore.customersStore as ServiceStore;
    const customers = (customersStore?.entities || []) as unknown as customer[];
    return customers.filter((c) => c.isActive);
  }

  // Computed - Structures with their items and conversion status (sorted by structureId)
  get structuresWithItems(): StructureWithStatus[] {
    const structures = this.quoteStructures;
    const items = this.quoteLineItems;

    const result = structures.map((structure) => {
      const structureItems = items.filter((item) => item.structureId === structure.id);
      const conversionInfo = this.convertedStructureMap.get(structure.id);

      return {
        id: structure.id,
        structureId: structure.structureId,
        structureCodeId: structure.structureCodeId,
        items: structureItems,
        isConverted: !!conversionInfo,
        salesOrderNumber: conversionInfo?.orderNumber,
        totalCost: structureItems.reduce((sum, item) => sum + (item.extendedCost || 0), 0),
      };
    });

    // Sort by structureId naturally (MH-1, MH-2, MH-10 instead of MH-1, MH-10, MH-2)
    return result.sort((a, b) =>
      a.structureId.localeCompare(b.structureId, undefined, { numeric: true, sensitivity: "base" })
    );
  }

  // Computed - Unconverted structures (available for selection)
  get unconvertedStructures(): StructureWithStatus[] {
    return this.structuresWithItems.filter((s) => !s.isConverted);
  }

  // Computed - Converted structures
  get convertedStructures(): StructureWithStatus[] {
    return this.structuresWithItems.filter((s) => s.isConverted);
  }

  // Computed - Check if all unconverted structures are selected
  get allUnconvertedStructuresSelected(): boolean {
    const unconverted = this.unconvertedStructures;
    return unconverted.length > 0 && unconverted.every((s) => this.selectedStructureIds.includes(s.id));
  }

  // Computed - Check if some unconverted structures are selected (for indeterminate checkbox)
  get someStructuresSelected(): boolean {
    const unconverted = this.unconvertedStructures;
    const selectedCount = unconverted.filter((s) => this.selectedStructureIds.includes(s.id)).length;
    return selectedCount > 0 && selectedCount < unconverted.length;
  }

  // Computed - Can convert (validation)
  // Only requires customer selection and at least one structure selected
  // Anticipation date and priority are optional
  get canConvert(): boolean {
    return (
      this.selectedStructureIds.length > 0 &&
      this.selectedCustomerId !== ""
    );
  }

  // Computed - Selected structures count and total cost
  get selectedStructuresCount(): number {
    return this.selectedStructureIds.length;
  }

  get selectedStructuresTotal(): number {
    return this.structuresWithItems
      .filter((s) => this.selectedStructureIds.includes(s.id))
      .reduce((sum, s) => sum + s.totalCost, 0);
  }

  // Computed - All structures converted
  get allStructuresConverted(): boolean {
    return this.structuresWithItems.length > 0 && this.unconvertedStructures.length === 0;
  }

  // Helper to get readable structure name from UUID
  getStructureName = (structureUuid: string | undefined): string => {
    if (!structureUuid) return "Ungrouped";
    return this.structureNameMap.get(structureUuid) || structureUuid;
  };

  // Computed - Line items grouped by structure (using readable names)
  get lineItemsGroupedByStructure(): Record<string, QuoteLineItemType[]> {
    const items = this.quoteLineItems;
    if (items.length === 0) return {};

    const grouped: Record<string, QuoteLineItemType[]> = {};
    items.forEach((item) => {
      const key = this.getStructureName(item.structureId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });

    return grouped;
  }

  // Computed - Get sorted structure group keys
  get structureGroups(): string[] {
    const keys = Object.keys(this.lineItemsGroupedByStructure);
    // Sort with 'Ungrouped' at the end
    return keys.sort((a, b) => {
      if (a === "Ungrouped") return 1;
      if (b === "Ungrouped") return -1;
      return a.localeCompare(b);
    });
  }

  // Computed - Items that have NOT been converted yet
  get unconvertedLineItems(): QuoteLineItemType[] {
    return this.quoteLineItems.filter((item) => !this.conversionMap.has(item.id as string));
  }

  // Computed - Items that HAVE been converted
  get convertedLineItems(): QuoteLineItemType[] {
    return this.quoteLineItems.filter((item) => this.conversionMap.has(item.id as string));
  }

  // Computed - Unconverted items grouped by structure (using readable names)
  get unconvertedItemsGroupedByStructure(): Record<string, QuoteLineItemType[]> {
    const items = this.unconvertedLineItems;
    if (items.length === 0) return {};

    const grouped: Record<string, QuoteLineItemType[]> = {};
    items.forEach((item) => {
      const key = this.getStructureName(item.structureId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });

    return grouped;
  }

  // Computed - Check if all items in a structure are selected
  isStructureFullySelected = (structureId: string): boolean => {
    const items = this.unconvertedItemsGroupedByStructure[structureId] || [];
    return items.length > 0 && items.every((item) => this.selectedLineItemIds.includes(item.id as string));
  };

  // Computed - Check if some items in a structure are selected (indeterminate state)
  isStructurePartiallySelected = (structureId: string): boolean => {
    const items = this.unconvertedItemsGroupedByStructure[structureId] || [];
    const selectedCount = items.filter((item) => this.selectedLineItemIds.includes(item.id as string)).length;
    return selectedCount > 0 && selectedCount < items.length;
  };

  // Computed - Check if all unconverted items are selected
  get allUnconvertedSelected(): boolean {
    const unconverted = this.unconvertedLineItems;
    return unconverted.length > 0 && unconverted.every((item) => this.selectedLineItemIds.includes(item.id as string));
  }

  // Computed - Indeterminate state for global select all
  get someUnconvertedSelected(): boolean {
    const unconverted = this.unconvertedLineItems;
    const selectedCount = unconverted.filter((item) => this.selectedLineItemIds.includes(item.id as string)).length;
    return selectedCount > 0 && selectedCount < unconverted.length;
  }

  // Computed - Total cost of selected items
  get selectedItemsTotal(): number {
    return this.quoteLineItems
      .filter((item) => this.selectedLineItemIds.includes(item.id as string))
      .reduce((sum, item) => sum + (item.extendedCost || 0), 0);
  }

  // Computed - Number of selected items
  get selectedItemsCount(): number {
    return this.selectedLineItemIds.length;
  }

  // Computed - Existing sales orders info for tooltip display
  get existingSalesOrdersForQuote(): Array<{ id: number | string; orderNumber: string; itemCount: number; total: number }> {
    return this.createdSalesOrdersForQuote.map((so) => ({
      id: so.id,
      orderNumber: so.orderNumber,
      itemCount: so.lineItemIds?.length || 0,
      total: (so.lineItemIds || []).reduce((sum, itemId) => {
        const item = this.quoteLineItems.find((i) => i.id === itemId);
        return sum + (item?.extendedCost || 0);
      }, 0),
    }));
  }

  // Computed - Whether the quote has any existing sales orders
  get hasExistingSalesOrders(): boolean {
    return this.createdSalesOrdersForQuote.length > 0;
  }

  // Computed - Whether all items have been converted
  get allItemsConverted(): boolean {
    return this.quoteLineItems.length > 0 && this.unconvertedLineItems.length === 0;
  }

  // Computed - Quote conversion status
  get quoteConversionStatus(): "not_started" | "partially_converted" | "fully_converted" {
    if (this.convertedLineItems.length === 0) return "not_started";
    if (this.unconvertedLineItems.length === 0) return "fully_converted";
    return "partially_converted";
  }

  // Actions - UI State Management
  setCurrentStep = (step: number) => {
    this.currentStep = step;
  };

  setSelectedQuote = (quoteId: string | number) => {
    this.selectedQuoteId = quoteId;
  };

  setError = (_operation: string, error: string) => {
    this.error = error;
  };

  clearError = () => {
    this.error = null;
  };

  setPanelTableAssignment = (panelId: string, tableNumber: string) => {
    this.panelTableAssignments[panelId] = tableNumber;
  };

  getPanelTableAssignment = (panelId: string): string => {
    return this.panelTableAssignments[panelId] || "";
  };

  // Actions - Line Item Selection
  toggleLineItemSelection = (itemId: string) => {
    const index = this.selectedLineItemIds.indexOf(itemId);
    if (index >= 0) {
      this.selectedLineItemIds.splice(index, 1);
    } else {
      this.selectedLineItemIds.push(itemId);
    }
  };

  selectAllInStructure = (structureId: string) => {
    const items = this.unconvertedItemsGroupedByStructure[structureId] || [];
    items.forEach((item) => {
      if (!this.selectedLineItemIds.includes(item.id as string)) {
        this.selectedLineItemIds.push(item.id as string);
      }
    });
  };

  deselectAllInStructure = (structureId: string) => {
    const items = this.unconvertedItemsGroupedByStructure[structureId] || [];
    const itemIds = items.map((item) => item.id as string);
    this.selectedLineItemIds = this.selectedLineItemIds.filter((id) => !itemIds.includes(id));
  };

  toggleStructureSelection = (structureId: string) => {
    if (this.isStructureFullySelected(structureId)) {
      this.deselectAllInStructure(structureId);
    } else {
      this.selectAllInStructure(structureId);
    }
  };

  selectAllUnconverted = () => {
    this.selectedLineItemIds = this.unconvertedLineItems.map((item) => item.id as string);
  };

  deselectAll = () => {
    this.selectedLineItemIds = [];
  };

  toggleSelectAll = () => {
    if (this.allUnconvertedSelected) {
      this.deselectAll();
    } else {
      this.selectAllUnconverted();
    }
  };

  // Actions - Structure Expansion
  toggleStructureExpanded = (structureId: string) => {
    if (this.expandedStructures.has(structureId)) {
      this.expandedStructures.delete(structureId);
    } else {
      this.expandedStructures.add(structureId);
    }
  };

  expandAllStructures = () => {
    this.structureGroups.forEach((group) => this.expandedStructures.add(group));
  };

  collapseAllStructures = () => {
    this.expandedStructures.clear();
  };

  // Actions - Post-Conversion Flow
  setShowPostConversionPrompt = (show: boolean) => {
    this.showPostConversionPrompt = show;
  };

  continueConvertingMore = () => {
    this.showPostConversionPrompt = false;
    this.selectedLineItemIds = [];
    this.selectedStructureIds = [];
    // Stay on Step 2, user can select more items/structures
  };

  finishConversion = () => {
    this.showPostConversionPrompt = false;
    this.currentStep = 3; // Go to completion step
  };

  // Actions - Structure Selection for API-based conversion (NEW)
  toggleStructureIdSelection = (structureId: string) => {
    const index = this.selectedStructureIds.indexOf(structureId);
    if (index >= 0) {
      this.selectedStructureIds.splice(index, 1);
    } else {
      this.selectedStructureIds.push(structureId);
      // Initialize settings when selecting a structure
      this.initializeStructureSettings(structureId);
    }
  };

  selectAllStructures = () => {
    this.selectedStructureIds = this.unconvertedStructures.map((s) => s.id);
    // Initialize settings for all selected structures
    this.selectedStructureIds.forEach((id) => this.initializeStructureSettings(id));
  };

  deselectAllStructures = () => {
    this.selectedStructureIds = [];
  };

  toggleSelectAllStructures = () => {
    if (this.allUnconvertedStructuresSelected) {
      this.deselectAllStructures();
    } else {
      this.selectAllStructures();
    }
  };

  // Actions - Conversion Form Fields (NEW)
  setSelectedCustomerId = (customerId: string) => {
    this.selectedCustomerId = customerId;
  };

  setPurchaseOrderNumber = (poNumber: string) => {
    this.purchaseOrderNumber = poNumber;
  };

  setConversionNotes = (notes: string) => {
    this.conversionNotes = notes;
  };

  // Per-structure settings methods
  getStructureSettings = (structureId: string): { anticipationDate: string; priority: number } => {
    return this.structureSettings.get(structureId) || { anticipationDate: "", priority: 0 };
  };

  setStructureAnticipationDate = (structureId: string, date: string) => {
    const current = this.structureSettings.get(structureId) || { anticipationDate: "", priority: 0 };
    this.structureSettings.set(structureId, { ...current, anticipationDate: date });
  };

  setStructurePriority = (structureId: string, priority: number) => {
    const current = this.structureSettings.get(structureId) || { anticipationDate: "", priority: 0 };
    this.structureSettings.set(structureId, { ...current, priority: Math.max(1, Math.min(10, priority)) });
  };

  // Initialize settings for a structure with empty defaults (no auto-fill)
  initializeStructureSettings = (structureId: string) => {
    if (!this.structureSettings.has(structureId)) {
      this.structureSettings.set(structureId, {
        anticipationDate: "",
        priority: 0,
      });
    }
  };

  // Toggle grouping mode
  setGroupStructuresIntoOne = (value: boolean) => {
    this.groupStructuresIntoOne = value;
  };

  toggleGroupingMode = () => {
    this.groupStructuresIntoOne = !this.groupStructuresIntoOne;
  };

  reset = () => {
    this.currentStep = 1;
    this.selectedQuoteId = undefined;
    this.activeSalesOrderId = undefined;
    this.createdPanelIds = [];
    this.panelTableAssignments = {};
    this.scheduleDate = null;
    this.error = null;
    this.isLoading = false;
    // Reset selection state
    this.selectedLineItemIds = [];
    this.conversionMap.clear();
    this.createdSalesOrdersForQuote = [];
    this.expandedStructures.clear();
    this.showPostConversionPrompt = false;
    // Reset structure selection and form fields (NEW)
    this.selectedStructureIds = [];
    this.selectedCustomerId = "";
    this.purchaseOrderNumber = "";
    this.conversionNotes = "";
    this.structureSettings.clear();
    this.convertedStructureMap.clear();
    this.lastConversionResponse = null;
    this.groupStructuresIntoOne = true;
  };

  // Initialize for dialog context - auto-selects quote for project and skips to convert step
  initializeForProject = async (projectId: string) => {
    this.reset();
    this.isLoading = true;
    this.error = null;

    try {
      // Load required data
      await this.loadQuotes();

      runInAction(() => {
        // Find quote for this project
        const manholeQuotesStore = this.rootStore.manholeQuotesStore as ServiceStore;
        const allQuotes = (manholeQuotesStore?.entities || []) as unknown as quotes[];
        const quote = allQuotes.find((q) => q.manholeProjectId === projectId);

        if (quote) {
          this.selectedQuoteId = quote.id;
          this.currentStep = 2; // Skip to "Convert" step
        } else {
          this.error = "No quote found for this project. Please create a quote first.";
        }
      });

      // Load conversion data if quote was found
      if (this.selectedQuoteId) {
        await this.loadConversionData();
      }
    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to initialize conversion";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Async Actions
  loadQuotes = async () => {
    this.isLoading = true;

    try {
      const manholeQuotesStore = this.rootStore.manholeQuotesStore as ServiceStore;
      const manholeQuoteItemsStore = this.rootStore.manholeQuoteItemsStore as ServiceStore;
      const manholeStructuresStore = this.rootStore.manholeStructuresStore as ServiceStore;
      const customersStore = this.rootStore.customersStore as ServiceStore;

      // Load quotes, items, structures, and customers for the conversion workflow
      await Promise.all([
        manholeQuotesStore.find({ query: {} }),
        manholeQuoteItemsStore.find({ query: {} }),
        manholeStructuresStore.find({ query: {} }),
        customersStore.find({ query: { isActive: true } }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to load quotes";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Load existing conversion data when a quote is selected
  loadConversionData = async () => {
    if (!this.selectedQuoteId) return;

    try {
      const salesOrderLineItemsStore = this.rootStore.salesOrderLineItemsStore as ServiceStore;
      const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;

      // Get all quote item IDs for this quote
      const quoteItemIds = this.quoteLineItems.map((i) => i.id as string);

      // Fetch sales orders for this quote (using manholeQuoteId field)
      const quote = this.selectedQuote;
      if (quote) {
        await salesOrdersStore.find({ query: { manholeQuoteId: quote.id } });
      }

      // Get all SO line items that reference this quote's items
      const allSoLineItems = (salesOrderLineItemsStore?.entities || []) as unknown as SalesOrderLineItemType[];
      const linkedSoLineItems = allSoLineItems.filter(
        (item) => item.quoteLineItemId && quoteItemIds.includes(String(item.quoteLineItemId))
      );

      // Get sales orders that have manholeQuoteId matching this quote
      const allSalesOrders = (salesOrdersStore?.entities || []) as unknown as Array<salesOrders & { manholeQuoteId?: string; manholeStructureId?: string }>;
      const quoteSalesOrders = allSalesOrders.filter(
        (so) => (so as any).manholeQuoteId === String(this.selectedQuoteId)
      );

      // Build conversion map and createdSalesOrdersForQuote
      runInAction(() => {
        this.conversionMap.clear();
        this.createdSalesOrdersForQuote = [];
        this.convertedStructureMap.clear();

        // Build structure conversion map from sales orders with manholeStructureId
        for (const so of quoteSalesOrders) {
          const structureId = (so as any).manholeStructureId;
          if (structureId) {
            this.convertedStructureMap.set(structureId, {
              salesOrderId: String(so.id),
              orderNumber: (so as any).orderNumber || so.soNumber || `SO-${so.id}`,
              convertedAt: so.createdAt,
            });

            this.createdSalesOrdersForQuote.push({
              id: so.id,
              orderNumber: (so as any).orderNumber || so.soNumber || `SO-${so.id}`,
              structureId: structureId,
              structureLabel: this.getStructureName(structureId),
            });
          }
        }

        // Also build line item conversion map from SO line items (for backward compatibility)
        const soGroups = new Map<number, string[]>();
        for (const soLineItem of linkedSoLineItems) {
          const so = allSalesOrders.find((s) => s.id === soLineItem.salesOrderId);
          if (so && soLineItem.quoteLineItemId) {
            const quoteLineItemId = String(soLineItem.quoteLineItemId);

            this.conversionMap.set(quoteLineItemId, {
              salesOrderId: so.id as number,
              orderNumber: (so as any).orderNumber || so.soNumber || `SO-${so.id}`,
              convertedAt: (soLineItem as any).createdAt || new Date().toISOString(),
            });

            const soId = so.id as number;
            if (!soGroups.has(soId)) {
              soGroups.set(soId, []);
            }
            soGroups.get(soId)!.push(quoteLineItemId);
          }
        }

        // Auto-expand all structures for better UX
        this.expandAllStructures();
      });
    } catch (error) {
      console.error("Failed to load conversion data:", error);
    }
  };

  // NEW: Convert selected structures using the manhole-quote-converter API
  // Uses structureGroups format - grouping mode determines if all go into one SO or separate SOs
  convertSelectedStructures = async () => {
    if (!this.canConvert) {
      this.error = "Please fill in all required fields and select at least one structure";
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const quote = this.selectedQuote;
      if (!quote) throw new Error("Quote not found");

      // Get the converter service store
      const converterStore = this.rootStore.manholeQuoteConverterStore as ServiceStore;
      if (!converterStore?.create) {
        throw new Error("Manhole quote converter service not available");
      }

      // Build structureGroups based on grouping mode
      // Each structure group now includes per-structure anticipationDate and priority
      // If groupStructuresIntoOne: all selected structures go into ONE group (one SO)
      // Otherwise: each structure gets its own group (separate SOs)
      const structureGroups = this.groupStructuresIntoOne
        ? [{
            structureIds: this.selectedStructureIds,
            orderNotes: this.conversionNotes || undefined,
            // For grouped structures, collect all settings (backend will use these per line item)
            // Only include anticipationDate if it's a valid non-empty string
            structureSettings: this.selectedStructureIds.map((id) => {
              const settings = this.getStructureSettings(id);
              return {
                structureId: id,
                ...(settings.anticipationDate ? { anticipationDate: settings.anticipationDate } : {}),
                priority: settings.priority,
              };
            }),
          }]
        : this.selectedStructureIds.map((id) => {
            const settings = this.getStructureSettings(id);
            return {
              structureIds: [id],
              // Only include anticipationDate if it's a valid non-empty string
              ...(settings.anticipationDate ? { anticipationDate: settings.anticipationDate } : {}),
              priority: settings.priority,
            };
          });

      // Call the manhole-quote-converter API with structureGroups format
      // Note: requiredDate and priority are now per-structure in structureGroups
      const response = (await converterStore.create({
        quoteId: quote.id,
        customerId: this.selectedCustomerId,
        purchaseOrderNumber: this.purchaseOrderNumber || undefined,
        notes: this.conversionNotes || undefined,
        structureGroups,
      })) as unknown as manholeQuoteConversionResponse;

      runInAction(() => {
        this.lastConversionResponse = response;

        // Update tracking from response - now handles multiple structures per SO
        response.salesOrders.forEach((so) => {
          // Each SO can have multiple structures (structureIds is now an array)
          const structureIds = so.structureIds || [so.structureId].filter(Boolean);
          const structureLabels = so.structureLabels || [so.structureLabel].filter(Boolean);

          // Add each structure to converted map
          structureIds.forEach((structId: string) => {
            this.convertedStructureMap.set(structId, {
              salesOrderId: so.orderId,
              orderNumber: so.orderNumber,
              convertedAt: new Date().toISOString(),
            });
          });

          // Add to created sales orders list with combined labels
          this.createdSalesOrdersForQuote.push({
            id: so.orderId,
            orderNumber: so.orderNumber,
            structureId: structureIds[0], // Primary structure
            structureLabel: structureLabels.join(", "), // Combined labels
          });
        });

        // Clear selection
        this.selectedStructureIds = [];

        // Show prompt or complete based on status
        if (response.quoteConversionStatus === "complete") {
          this.currentStep = 3;
        } else {
          this.showPostConversionPrompt = true;
        }
      });

      const soCount = response.salesOrders.length;
      const structCount = this.groupStructuresIntoOne ? this.selectedStructureIds.length : soCount;
      this.rootStore.notificationStore?.addNotification({
        message: `Created ${soCount} sales order(s) with ${response.totalProductsCreated} products from ${structCount} structure(s)`,
        options: { variant: "success" },
      });

      // Show any warnings
      if (response.warnings && response.warnings.length > 0) {
        response.warnings.forEach((warning) => {
          this.rootStore.notificationStore?.addNotification({
            message: warning,
            options: { variant: "warning" },
          });
        });
      }
    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to convert structures to sales orders";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  convertQuoteToSalesOrder = async (quoteId: number | string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // First try to find quote in store - manholeQuote uses string IDs
      const manholeQuotesStore = this.rootStore.manholeQuotesStore as ServiceStore;
      const allQuotes = (manholeQuotesStore?.entities || []) as unknown as quotes[];
      const quote = allQuotes.find(
        (q) => q.id === String(quoteId) || q.id === quoteId
      );

      if (!quote) {
        throw new Error("Quote not found");
      }

      // Generate sales order number
      const timestamp = Date.now();
      const soNumber = `SO-${new Date().getFullYear()}-${String(
        timestamp
      ).slice(-6)}`;

      // Create sales order from manhole quote using API types
      // The API schema expects: orderNumber, customerId, orderDate, status
      const newSalesOrder = {
        orderNumber: soNumber,
        customerId: String(quote.accountId), // API expects string
        jobName: quote.jobName || "",
        orderDate: new Date().toISOString().split("T")[0], // API expects date string
        status: "draft" as const, // API enum: 'draft' | 'released' | 'in_progress' | 'completed' | 'cancelled'
      };

      // Try to create the sales order using the service store
      const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;
      let createdSO: any;
      const quoteLineItems = this.quoteLineItems;

      // Check if ServiceStore can create sales orders
      if (salesOrdersStore?.create) {
        try {
          createdSO = await salesOrdersStore.create(newSalesOrder);
        } catch (error) {
          console.log("ServiceStore not available, using mock data for demo");
          createdSO = null; // Fall through to mock
        }
      }

      // If ServiceStore didn't work, use mock data for demo
      if (!createdSO) {
        console.log("Using mock sales order creation for demo");

        // Create mock sales order
        const mockSoId = this.nextMockId++;
        createdSO = {
          id: mockSoId,
          ...newSalesOrder,
          soNumber: newSalesOrder.orderNumber, // Add soNumber for backward compatibility in UI
          totalAmount: 0,
          currency: "USD",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as salesOrders;

        // Store in mock storage
        this.mockSalesOrders.push(createdSO);

        // Create mock sales order line items
        const salesOrderLineItemsStore = this.rootStore.salesOrderLineItemsStore as ServiceStore;

        for (const quoteItem of quoteLineItems) {
          // manholeQuoteItem uses different field names
          const soLineItem: any = {
            id: this.nextMockId++,
            salesOrderId: mockSoId,
            quoteLineItemId: quoteItem.id,
            lineNumber: quoteItem.lineNumber,
            itemType: quoteItem.itemType,
            productCode: quoteItem.componentCode || "",
            description: quoteItem.description || quoteItem.componentName || "",
            longDescription: "",
            specifications: "",
            orderedQuantity: quoteItem.quantity || 1,
            unitPrice: quoteItem.unitCost || 0,
            discountPercent: 0,
            totalPrice: quoteItem.extendedCost || 0,
            notes: "",
            status: "Ordered",
          };

          // Try ServiceStore first, if it fails use mock
          if (salesOrderLineItemsStore?.create) {
            try {
              await salesOrderLineItemsStore.create(soLineItem);
            } catch (lineError) {
              console.log("Using mock line item creation");
              this.mockSalesOrderLineItems.push(soLineItem);
            }
          } else {
            this.mockSalesOrderLineItems.push(soLineItem);
          }
        }

        // Simulate async delay
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        // Use real ServiceStore for line items if sales order creation worked
        const salesOrderLineItemsStore = this.rootStore.salesOrderLineItemsStore as ServiceStore;

        for (const quoteItem of quoteLineItems) {
          // manholeQuoteItem uses different field names
          const soLineItem: Record<string, unknown> = {
            salesOrderId:
              typeof createdSO.id === "string"
                ? parseInt(createdSO.id, 10)
                : createdSO.id,
            quoteLineItemId: quoteItem.id,
            lineNumber: quoteItem.lineNumber,
            itemType: quoteItem.itemType,
            productCode: quoteItem.componentCode || "",
            description: quoteItem.description || quoteItem.componentName || "",
            longDescription: "",
            specifications: "",
            orderedQuantity: quoteItem.quantity || 1,
            unitPrice: quoteItem.unitCost || 0,
            discountPercent: 0,
            totalPrice: quoteItem.extendedCost || 0,
            notes: "",
            status: "Ordered",
          };

          try {
            await salesOrderLineItemsStore.create(soLineItem);
          } catch (lineError) {
            console.error("Failed to create sales order line item:", lineError);
          }
        }
      }

      // Generate panels from quote line items using panel service
      // manholeQuote uses manholeProjectId, not jobId
      // For now, generate a job number from the project ID or use a default
      let jobNumber = 2024001;
      const projectId = quote.manholeProjectId as string | undefined;
      if (projectId) {
        // Use last 6 digits of project UUID as job number
        const projectIdNum = parseInt(projectId.replace(/-/g, '').slice(-6), 16);
        jobNumber = projectIdNum || 2024001;
      }

      console.log("Generating panels for job number:", jobNumber);
      await this.generatePanelsFromLineItems(
        quoteLineItems,
        jobNumber
      );

      runInAction(() => {
        this.activeSalesOrderId =
          typeof createdSO.id === "string"
            ? parseInt(createdSO.id, 10)
            : createdSO.id;
        this.currentStep = 2; // Move to next step
      });

      // Update manhole quote status to accepted (converted)
      try {
        // manholeQuote uses string UUIDs for IDs
        await manholeQuotesStore.patch(quote.id as any, {
          status: "accepted",
        });
      } catch (patchError) {
        console.error("Failed to update quote status:", patchError);
      }

      this.rootStore.notificationStore?.addNotification({
        message: `Sales Order ${createdSO.orderNumber || createdSO.soNumber} created successfully`,
        options: { variant: "success" },
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          (error as Error).message || "Failed to convert quote to sales order";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Convert only selected items to a sales order (for partial conversion)
  convertSelectedItemsToSalesOrder = async () => {
    if (this.selectedLineItemIds.length === 0) {
      this.error = "Please select at least one item to convert";
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const quote = this.selectedQuote;
      if (!quote) throw new Error("Quote not found");

      // Get only selected items
      const selectedItems = this.quoteLineItems.filter((item) =>
        this.selectedLineItemIds.includes(item.id as string)
      );

      // Generate sales order number
      const timestamp = Date.now();
      const soNumber = `SO-${new Date().getFullYear()}-${String(timestamp).slice(-6)}`;

      // Create sales order
      const newSalesOrder = {
        orderNumber: soNumber,
        customerId: String(quote.accountId),
        jobName: quote.jobName || "",
        orderDate: new Date().toISOString().split("T")[0],
        status: "draft" as const,
      };

      const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;
      const salesOrderLineItemsStore = this.rootStore.salesOrderLineItemsStore as ServiceStore;
      const manholeQuotesStore = this.rootStore.manholeQuotesStore as ServiceStore;
      let createdSO: any;

      // Try to create the sales order using the service store
      if (salesOrdersStore?.create) {
        try {
          createdSO = await salesOrdersStore.create(newSalesOrder);
        } catch (error) {
          console.log("ServiceStore not available, using mock data for demo");
          createdSO = null;
        }
      }

      // If ServiceStore didn't work, use mock data for demo
      if (!createdSO) {
        const mockSoId = this.nextMockId++;
        createdSO = {
          id: mockSoId,
          ...newSalesOrder,
          soNumber: newSalesOrder.orderNumber,
          totalAmount: selectedItems.reduce((sum, item) => sum + (item.extendedCost || 0), 0),
          currency: "USD",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as salesOrders;

        this.mockSalesOrders.push(createdSO);
      }

      const soId = typeof createdSO.id === "string" ? parseInt(createdSO.id, 10) : createdSO.id;
      const selectedItemIds = selectedItems.map((item) => item.id as string);

      // Create sales order line items for selected items only
      for (const quoteItem of selectedItems) {
        const soLineItem: any = {
          id: this.nextMockId++,
          salesOrderId: soId,
          quoteLineItemId: quoteItem.id,
          lineNumber: quoteItem.lineNumber,
          itemType: quoteItem.itemType,
          productCode: quoteItem.componentCode || "",
          description: quoteItem.description || quoteItem.componentName || "",
          orderedQuantity: quoteItem.quantity || 1,
          unitPrice: quoteItem.unitCost || 0,
          totalPrice: quoteItem.extendedCost || 0,
          status: "Ordered",
          createdAt: new Date().toISOString(),
        };

        if (salesOrderLineItemsStore?.create) {
          try {
            await salesOrderLineItemsStore.create(soLineItem);
          } catch (lineError) {
            console.log("Using mock line item creation");
            this.mockSalesOrderLineItems.push(soLineItem);
          }
        } else {
          this.mockSalesOrderLineItems.push(soLineItem);
        }
      }

      // Generate panels from selected items
      let jobNumber = 2024001;
      const projectId = quote.manholeProjectId as string | undefined;
      if (projectId) {
        const projectIdNum = parseInt(projectId.replace(/-/g, "").slice(-6), 16);
        jobNumber = projectIdNum || 2024001;
      }
      await this.generatePanelsFromLineItems(selectedItems, jobNumber);

      // Update conversion tracking
      runInAction(() => {
        // Record which items were converted
        selectedItems.forEach((item) => {
          this.conversionMap.set(item.id as string, {
            salesOrderId: soId,
            orderNumber: soNumber,
            convertedAt: new Date().toISOString(),
          });
        });

        // Add to created sales orders list
        this.createdSalesOrdersForQuote.push({
          id: soId,
          orderNumber: soNumber,
          lineItemIds: selectedItemIds,
        });

        // Clear selection
        this.selectedLineItemIds = [];

        // Update active sales order ID
        this.activeSalesOrderId = soId;
      });

      // Update quote status based on conversion state
      const newStatus = this.allItemsConverted ? "accepted" : "partially_converted";
      try {
        await manholeQuotesStore.patch(quote.id as any, { status: newStatus });
      } catch (patchError) {
        console.error("Failed to update quote status:", patchError);
      }

      this.rootStore.notificationStore?.addNotification({
        message: `Sales Order ${soNumber} created with ${selectedItems.length} items`,
        options: { variant: "success" },
      });

      // Show post-conversion prompt if items remain, otherwise go to completion
      runInAction(() => {
        if (this.unconvertedLineItems.length > 0) {
          this.showPostConversionPrompt = true;
        } else {
          this.currentStep = 3; // Go to completion step
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to create sales order";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Generate panels from sales order line items
  private generatePanelsFromLineItems = async (
    lineItems: QuoteLineItemType[],
    jobNumber?: number
  ) => {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    let panelCount = 1;
    const actualJobNumber = jobNumber || 10; // Default job number if not provided

    for (const lineItem of lineItems) {
      // Generate panels based on quantity (limit to reasonable number for demo)
      const panelsToCreate = Math.min(lineItem.quantity, 5);

      for (let i = 0; i < panelsToCreate; i++) {
        // Generate random identifiers following the pattern from PurchaseOrderViewStore
        const randomControlNumber = Math.floor(Math.random() * 9000) + 1000;
        const seriesLetter = String.fromCharCode(
          65 + Math.floor(Math.random() * 26)
        );
        const seriesNumber = String(
          Math.floor(Math.random() * 99) + 1
        ).padStart(2, "0");
        const markSeries = `${seriesLetter}${seriesNumber}`;

        // Generate realistic dimensions
        const panelLength = Math.floor(Math.random() * 16) + 8; // 8-24 feet
        const panelHeight = Math.floor(Math.random() * 4) + 8; // 8-12 feet
        const panelThickness = Math.floor(Math.random() * 4) + 4; // 4-8 inches

        // Calculate realistic values
        const totalSqFt = panelLength * panelHeight + Math.random() * 10;
        const volume = (totalSqFt * (panelThickness / 12)) / 27; // Convert to cubic yards
        const assembledPanelWeight = totalSqFt * (120 + Math.random() * 30); // 120-150 lbs per sq ft

        // Production details
        const finishTypes = [
          "Smooth",
          "Textured",
          "Architectural",
          "Sandblasted",
          "Exposed Aggregate",
        ];
        const castingTypes = [
          "Standard",
          "Insulated",
          "Structural",
          "Architectural",
        ];
        const productTypes = ["PWP", "IWP", "SBP", "DFP", "SSB"];

        const panelData: Panel = {
          panelId: panelCount.toString().padStart(3, "0"),
          controlNumber: randomControlNumber.toString(),
          controlUUID: `${actualJobNumber}${randomControlNumber}`,
          markNumber: `M-${markSeries}-${i + 1}`,
          markUUID: `${actualJobNumber}M-${markSeries}-${i + 1}`,
          jobNumber: actualJobNumber,
          // Note: jobId removed - panels use jobNumber, not jobId

          // Dimensions and specifications
          panelLength: `${panelLength}'-${Math.floor(Math.random() * 12)}"`,
          totalSqFt: Math.round(totalSqFt * 10) / 10,
          volume: Math.round(volume * 100) / 100,
          assembledPanelWeight: Math.round(assembledPanelWeight),

          // Production details
          finishType:
            finishTypes[Math.floor(Math.random() * finishTypes.length)],
          castingType:
            castingTypes[Math.floor(Math.random() * castingTypes.length)],
          productType:
            productTypes[Math.floor(Math.random() * productTypes.length)],

          // Manufacturing details
          moldNumber: `M${Math.floor(Math.random() * 99) + 1}`,
          fabricationSequence: `${Math.floor(Math.random() * 100) + 1}`,
          installationSequence: `${Math.floor(Math.random() * 100) + 1}`,

          // Mix design
          mixDesign1: `MIX-${Math.floor(Math.random() * 9) + 1}`,
          mix1SqFt: Math.round(totalSqFt * (0.7 + Math.random() * 0.3)),
          mix1Volume:
            Math.round(volume * (0.7 + Math.random() * 0.3) * 100) / 100,

          // Additional properties
          veneerSqFt: Math.round(totalSqFt * (0.8 + Math.random() * 0.2)),
          netExposedSqFt: Math.round(totalSqFt * (0.6 + Math.random() * 0.3)),
          totalMixCubicYd: Math.round(volume * 100) / 100,
          individualPanelWeight: Math.round(assembledPanelWeight * 0.95),

          // Dates - castingDate will be set later in workflow
          createdDate: new Date().toISOString().split("T")[0],
          updatedDate: new Date().toISOString().split("T")[0],
        };

        try {
          // Try to create panel using ServiceStore
          if (panelStore?.create) {
            const newPanel = await panelStore.create(panelData);
            if (newPanel && newPanel.id) {
              // Track created panel ID for filtering
              this.createdPanelIds.push(newPanel.id as number);
              console.log(
                `Panel ${newPanel.panelId} created with ID ${newPanel.id}`
              );
            }
          } else {
            throw new Error("No panel store available");
          }
        } catch (error) {
          // Fall back to mock panel creation
          console.log("Using mock panel creation for demo");
          const mockPanelId = this.nextMockId++;
          const mockPanel = {
            ...panelData,
            id: mockPanelId,
          } as Panel;

          this.mockPanels.push(mockPanel);
          this.createdPanelIds.push(mockPanelId);
          console.log(
            `Mock panel ${mockPanel.panelId} created with ID ${mockPanelId}`
          );
        }

        panelCount++;
      }
    }

    this.rootStore.notificationStore?.addNotification({
      message: `${panelCount - 1} products generated for sales order`,
      options: { variant: "success" },
    });
  };

  updatePanelCastingDate = async (panelId: number, castingDate: string) => {
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      await panelStore?.patch?.(panelId, { castingDate });

      this.rootStore.notificationStore?.addNotification({
        message: `Panel casting date updated`,
        options: { variant: "success" },
      });
    } catch (error) {
      console.error("Failed to update panel casting date:", error);
    }
  };

  updatePanelTableAssignment = async (panelId: number, tableNumber: string) => {
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      await panelStore?.patch?.(panelId, { tableNumber });

      this.rootStore.notificationStore?.addNotification({
        message: `Panel assigned to Table ${tableNumber}`,
        options: { variant: "success" },
      });
    } catch (error) {
      console.error("Failed to update panel table assignment:", error);
    }
  };

  scheduleProduction = async (data: ProductionScheduleData) => {
    this.isLoading = true;

    try {
      // In production: create production order using productionOrdersStore
      console.log("Scheduling production:", data);

      // Mock success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      runInAction(() => {
        this.currentStep = 4; // Move to complete
        this.scheduleDate = data.plannedStartDate;
      });

      this.rootStore.notificationStore?.addNotification({
        message: `Production scheduled for ${this.generatedPanels.length} panels`,
        options: { variant: "success" },
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          (error as Error).message || "Failed to schedule production";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Disposal method for proper cleanup
  dispose = () => {
    if (this.errorHandler) {
      this.errorHandler.dispose();
    }
  };
}
